import * as React from "react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

/**
 * Always renders a 12-hour hour/minute/AM-PM picker regardless of the user's
 * OS/browser locale — unlike native <input type="time">, whose displayed
 * format (12h vs 24h) is dictated by the device and is inconsistent across
 * users. The value/onChange contract stays 24-hour "HH:mm" so callers (and
 * the backend, which validates/stores that format) are unaffected.
 */

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1) // 1..12
const MINUTES = Array.from({ length: 60 }, (_, i) => i) // 0..59

type Period = "AM" | "PM"

function parse24Hour(value: string): { hour: number; minute: number; period: Period } {
  const match = /^(\d{2}):(\d{2})$/.exec(value)
  const [h, m] = match ? [Number(match[1]), Number(match[2])] : [0, 0]
  const period: Period = h >= 12 ? "PM" : "AM"
  const hour = h % 12 === 0 ? 12 : h % 12
  return { hour, minute: m, period }
}

function to24Hour(hour: number, minute: number, period: Period): string {
  const h = (hour % 12) + (period === "PM" ? 12 : 0)
  return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
}

export interface TimePicker12hProps {
  id?: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}

export function TimePicker12h({ id, value, onChange, disabled }: TimePicker12hProps) {
  const { hour, minute, period } = parse24Hour(value)

  return (
    <div id={id} className="flex gap-1.5">
      <Select
        disabled={disabled}
        value={String(hour)}
        onValueChange={(v) => onChange(to24Hour(Number(v), minute, period))}
      >
        <SelectTrigger className="w-[4.25rem]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {HOURS_12.map((h) => (
            <SelectItem key={h} value={String(h)}>
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        disabled={disabled}
        value={String(minute)}
        onValueChange={(v) => onChange(to24Hour(hour, Number(v), period))}
      >
        <SelectTrigger className="w-[4.25rem]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MINUTES.map((m) => (
            <SelectItem key={m} value={String(m)}>
              {String(m).padStart(2, "0")}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        disabled={disabled}
        value={period}
        onValueChange={(v) => onChange(to24Hour(hour, minute, v as Period))}
      >
        <SelectTrigger className="w-[4.25rem]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}
